import React, { useState } from 'react';
import { Check, Building2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useCompany } from '@/contexts/CompanyContext';
import { cn } from '@/lib/utils';

const CompanySelector: React.FC = () => {
  const {
    currentCompany,
    availableCompanies,
    switchCompany,
    loading
  } = useCompany();
  
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());
  if (loading) {
    return <Button variant="ghost" size="sm" disabled className="h-8 px-2">
        <Building2 className="h-4 w-4 mr-2" />
        <span className="text-sm">Loading...</span>
      </Button>;
  }

  if (!currentCompany && availableCompanies.length === 0) {
    return <Button variant="ghost" size="sm" className="h-8 px-2 cursor-default">
        <Building2 className="h-4 w-4 mr-2" />
        <span className="text-sm text-destructive">
          No Companies
        </span>
      </Button>;
  }

  // If user only has one company, show the company name without dropdown
  if (availableCompanies.length === 1) {
    const company = currentCompany || availableCompanies[0];
    return (
      <Button variant="ghost" size="sm" className="h-8 px-2 cursor-default">
        {company?.logo_url && !failedLogos.has(company.company_id) ? (
          <img 
            src={company.logo_url} 
            alt={company.company_name} 
            className="h-4 w-4 mr-2 rounded object-cover"
            onError={() => setFailedLogos(prev => new Set(prev).add(company.company_id))}
          />
        ) : (
          <Building2 className="h-4 w-4 mr-2" />
        )}
        <span className="text-sm font-medium">
          {company?.company_name}
        </span>
      </Button>
    );
  }
  return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2">
          {currentCompany?.logo_url && !failedLogos.has(currentCompany.company_id) ? (
            <img 
              src={currentCompany.logo_url} 
              alt={currentCompany.company_name} 
              className="h-4 w-4 mr-2 rounded object-cover"
              onError={() => setFailedLogos(prev => new Set(prev).add(currentCompany.company_id))}
            />
          ) : (
            <Building2 className="h-4 w-4 mr-2" />
          )}
          <span className="text-sm font-medium">
            {currentCompany?.company_name || 'Select Company'}
          </span>
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-popover">
        {availableCompanies.map(company => <DropdownMenuItem key={company.company_id} onClick={() => switchCompany(company.company_id)} className={cn("flex items-center justify-between cursor-pointer", company.is_active && "bg-accent")}>
            <div className="flex items-center gap-2 flex-1">
              {company.logo_url && !failedLogos.has(company.company_id) ? (
                <img 
                  src={company.logo_url} 
                  alt={company.company_name} 
                  className="h-5 w-5 rounded object-cover flex-shrink-0"
                  onError={() => setFailedLogos(prev => new Set(prev).add(company.company_id))}
                />
              ) : (
                <Building2 className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
              )}
              <span className="font-medium">{company.company_name}</span>
            </div>
            {company.is_active && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
};
export default CompanySelector;